import { createSlice } from "@reduxjs/toolkit";
import { eGlobalState } from "../../../_game/config/Game";

export type tDrawNumber = {
  isSelected: boolean;
  isConfirmed: boolean;
  isDrawed: boolean;
};

export interface iDrawTable {
  [key: number]: tDrawNumber;
}

const initialState = {
  COMM_STATUS: {
    bIsBusy: false,
    lastCommand: null,
    errorCode: 0,
    errorMessage: "OK",
  },
  SESSION_DATA: {
    sessionID: "",
    fixedID: "",
    country: "",
    isFun: true,
    //data about bets
    betIndex: 0,
    betLevels: [],

    currency: {
      symbol: "",
      before: true,
      code: "FUN",
      decimalSeparator: ".",
      thousandSeparator: ",",
      decimalsNumber: 2,
      coinsRatio: 1,
    },
    configuration: {
      buyToBonusAllowed: true,
      coinAllowed: true,
      fastSpinModeAllowed: true,
      gambleAllowed: true,
      minSpinSeconds: 0,
      realityCheckSettingsAllowed: true,
      showAutoplay: true,
      showStatsRequired: false,
      slamStopAllowed: true,
      RTP: 0,
    },
    // free promo bets are stored here:
    freeBets: {
      // marlipin:
      bonusRef1: "",
      bonusRef2: "",
      // spearhead:
      promoName: "",
      // common:
      remainingFreeSpins: 0,
      totalFreeSpins: 0,
      used: 0,
      amount: 0.0,
      winnings: 0.0,
    },
  },
  // internal up to date USER DATA
  USER_DATA: {
    credit: 0,
    // bet is default bet received on init, then updated when manually changing the bet
    bet: 0,
  },
  //#TODO: should be standardized if possible:
  GAME_DATA: {
    nextDrawNumber: 0,
    nextTimestamp: 0,
  },
  // status data represents data that's immediately updated in interfaces
  STATUS_DATA: {
    credit: 0,
    bet: 0,
    win: 0,
  },
  SETTINGS_DATA: {
    // is sound muted
    bSoundsMuted: false,
    // can I use spacebar to spin? Linked to options
    bAllowSpacebar: true,
  },
  GUI_DATA: {
    // shows you if there's any open menu, used for space to spin and for blocking input
    bMenuOpen: false,
    // Gets set by gameplay. shows if game is idle so we can interrupt with menus or other user actions
    bGameIsIdle: true,
    // Tells if the game booted completely (resume or not)
    bGameBooted: false,
    // Did we start the free rounds? that's true only after user accepts to start them
    freeRounds_bStarted: false,
    // saved total free spins win
    freeRounds_TotalWin: 0,
  },
  // settings referring to current game (doesn't reset)
  GAMEPLAY_SETTINGS: {
    bFastPlayOn: false, // is fast spin enabled?
    nAuto_RoundsSelectedLast: 40,
    nAutoplayRounds: 0, // how many autoplay rounds we still have to play
    nAuto_LossLimit: 0, // autoplay loss limit
    nAuto_WinLimit: 0, // autoplay win limit
    nAuto_SingeWin: 0, // autoplay single win
    nAuto_StartingCredit: 0, // autoplay starting credit
    bAuto_StopAtWin: false, // autoplay stops at any win
    bAuto_StopAtFs: false, //autoplay stops at free spins
    savedAutoplayData: {
      autoplayRounds: 0,
      beforeCredit: 0,
      stopAtWin: false,
      winLimit: 0,
      lossLimit: 0,
      singleWin: 0,
    },
  },
  // gameplay data that other components must link to (resets every spin/round)
  GAMEPLAY: {
    // global state of the reels, normal flow of the game:
    globalState: eGlobalState.IDLE,
    drawNumber: 0,
    nextDrawNumber: 0,
    bettingOpen: false,
    timer: 0,
    selectedNumbers: {} as iDrawTable,
    nextDraws: 1,
    isEnded: false, //for place bet
    drawStart: false,
    drawEnds: false,
    totalWinOnLastDraw: 0,
    drawResults: [],
    drawCurrentNumber: 0,
    tickets: [],
    failedBets: [],
    currentTicket: null,
    drawedNumbers: [],
    idleTime: 0,
    roundStart: false,
    history: [],
  },
};

export const gameSlice = createSlice({
  name: "game",
  initialState,
  reducers: {
    setServerResponse: (state, action) => {
      return {
        ...state,
        SESSION_DATA: { ...state.SESSION_DATA, ...action.payload.SESSION_DATA },
        USER_DATA: { ...state.USER_DATA, ...action.payload.USER_DATA },
        GAME_DATA: { ...state.GAME_DATA, ...action.payload.GAME_DATA },
        GAMEPLAY: { ...state.GAMEPLAY, ...action.payload.GAMEPLAY },
      };
    },
    setServerStatus: (state, action) => {
      return {
        ...state,
        COMM_STATUS: { ...state.COMM_STATUS, ...action.payload },
      };
    },
    setGUIData: (state, action) => {
      return {
        ...state,
        GUI_DATA: { ...state.GUI_DATA, ...action.payload },
      };
    },
    setSettingsData: (state, action) => {
      return {
        ...state,
        SETTINGS_DATA: { ...state.SETTINGS_DATA, ...action.payload },
      };
    },
    setUserData: (state, action) => {
      return {
        ...state,
        USER_DATA: { ...state.USER_DATA, ...action.payload },
      };
    },
    setStatusData: (state, action) => {
      return {
        ...state,
        STATUS_DATA: { ...state.STATUS_DATA, ...action.payload },
      };
    },
    setSessionData: (state, action) => {
      return {
        ...state,
        SESSION_DATA: { ...state.SESSION_DATA, ...action.payload },
      };
    },
    setGameplayData: (state, action) => {
      return {
        ...state,
        GAMEPLAY: { ...state.GAMEPLAY, ...action.payload },
      };
    },
    setGameplaySettings: (state, action) => {
      return {
        ...state,
        GAMEPLAY_SETTINGS: { ...state.GAMEPLAY_SETTINGS, ...action.payload },
      };
    },
  },
});

export const {
  setServerResponse,
  setServerStatus,
  setGUIData,
  setSettingsData,
  setUserData,
  setStatusData,
  setSessionData,
  setGameplayData,
  setGameplaySettings,
} = gameSlice.actions;

export default gameSlice.reducer;
